import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    CircularProgress,
    Typography,
    Box,
    Autocomplete,
    TextField,
    IconButton,
    Collapse,
    Tooltip,
    Paper,
    Stack,
    Chip,
    Divider,
    Alert,
    Snackbar,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button,
    InputAdornment,
    MenuItem,
    alpha,
    Fade,
    Zoom,
    Container,
    Card,
    CardContent,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Download,
    ExpandMore,
    ExpandLess,
    InsertDriveFile,
    Folder,
    Search,
    ClearAll,
    AccountBalance,
    Visibility,
    Cancel,
    CheckCircle,
    FilterList,
    Person,
    CalendarMonth,
    Category,
    ArrowUpward,
    ArrowDownward,
    Sync,
    Edit,
} from '@mui/icons-material';

import usePreview from '../../../hooks/comptable/usePreview';
import PreviewDialog from '../../../components/Comptable/dialogs/PreviewDialog';

const API = {
    LIST: '/api/comptable/clients-documents',
    DOWNLOAD: (id) => `/api/uploads/document/${id}`,
    ZIP: ({ clientId, type, year, month, stateFilter }) => {
        const params = new URLSearchParams({ clientId });
        if (type) params.set('type', type);
        if (year) params.set('year', String(year));
        if (month) params.set('month', String(month));
        if (stateFilter && stateFilter !== 'all') {
            params.set('stateFilter', stateFilter);
        }
        return `/api/uploads/download-zip?${params.toString()}`;
    },
};

const MONTHS_FR = [
    '',
    'Janvier',
    'Février',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Décembre',
];

const TYPE_LABELS = {
    emis: '📤 Certificats TEJ émis',
    'émis': '📤 Certificats TEJ émis',
    recu: '📥 Certificats TEJ reçus',
    'reçu': '📥 Certificats TEJ reçus',
    recus: '📥 Certificats TEJ reçus',
    'reçus': '📥 Certificats TEJ reçus',
    declaration: '📋 Déclarations employeur',
};

const DOC_STATE_OPTIONS = [
    { value: 'all', label: '📄 Tous les documents', icon: <InsertDriveFile /> },
    { value: 'active', label: '✅ Documents actifs', icon: <CheckCircle /> },
    { value: 'rectified', label: '✏️ Documents rectifiés', icon: <Edit /> },
    { value: 'cancelled', label: '❌ Documents annulés', icon: <Cancel /> },
];

const DOC_TYPE_OPTIONS = [
    { value: 'all', label: '📁 Tous les types' },
    { value: 'emis', label: '📤 Certificats émis' },
    { value: 'recu', label: '📥 Certificats reçus' },
    { value: 'declaration', label: '📋 Déclarations employeur' },
];

// Palette unifiée inspirée de GestionClient et LoginComptable
const usePalette = () => useMemo(
    () => ({
        navy: '#0B1E3C',
        navy2: '#102B55',
        teal: '#18B6B0',
        teal2: '#39D0C9',
        tealLight: '#E0F7F6',
        green: '#7BC043',
        gold: '#F3C15C',
        orange: '#FF9800',
        paper: '#FFFFFF',
        gradient: 'linear-gradient(135deg, #F8FAFC 0%, #EFF6FF 100%)',
        shadow: '0 10px 40px rgba(11, 30, 60, 0.08)',
        textPrimary: '#1E293B',
        textSecondary: '#64748B'
    }),
    []
);

// Fonction pour obtenir le nom d'affichage avec code client
const getClientDisplayName = (client) => {
    const code = client?.codeClient || '';
    if (client?.prenom && client?.nom) {
        return `${code} - ${client.prenom} ${client.nom}`;
    }
    return `${code} - ${client?.raisonSociale || client?._id || 'Client inconnu'}`;
};

// Fonction pour obtenir uniquement le nom sans code
const getClientNameOnly = (client) => {
    if (client?.prenom && client?.nom) {
        return `${client.prenom} ${client.nom}`;
    }
    return client?.raisonSociale || client?._id || 'Client inconnu';
};

// Fonction pour obtenir le code client
const getClientCode = (client) => {
    return client?.codeClient || '';
};

const labelForType = (t) =>
    TYPE_LABELS[(t || '').toString().toLowerCase()] || t || 'Autre';

const asType = (t = '') => {
    const s = String(t).trim().toLowerCase();
    if (s === 'emis') return 'emis';
    if (s === 'declaration') return 'declaration';
    return 'recu';
};

const buildStructure = (clients) =>
    clients.reduce((acc, { client, documents = [] }) => {
        const clientKey = getClientDisplayName(client);
        const bucket = (acc[clientKey] ||= {});
        documents.forEach((doc) => {
            const type = asType(doc?.classification?.type);
            const year = doc?.classification?.annee;
            const month = doc?.classification?.mois;
            if (year == null || month == null) return;
            (((bucket[type] ||= {})[year] ||= {})[month] ||= []).push(doc);
        });
        return acc;
    }, {});

const downloadBlob = async (response, fallbackName) => {
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = fallbackName;
    if (contentDisposition) {
        const match = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
        );
        if (match) fileName = decodeURIComponent(match[1].replace(/['"]/g, ''));
    }
    if (!fileName.toLowerCase().endsWith('.pdf')) fileName += '.pdf';

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const downloadAs = async (response, fallbackName = 'documents.zip') => {
    const cd = response.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    let fileName = fallbackName;
    if (m) fileName = decodeURIComponent(m[1].replace(/['"]/g, ''));
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// ========================= Sub-components avec style affiné ========================= //
const SectionRow = ({
    nodeKey,
    icon,
    label,
    count,
    isOpen,
    onToggle,
    children,
    right,
    level = 0,
}) => {
    const palette = usePalette();
    const bgColors = [
        alpha(palette.teal, 0.02),
        alpha(palette.navy, 0.02),
        alpha(palette.green, 0.02),
        alpha(palette.gold, 0.02),
    ];

    return (
        <Fade in timeout={300}>
            <Box component="li" sx={{ listStyle: 'none', mb: 1 }}>
                <Paper
                    elevation={0}
                    component="div"
                    role="button"
                    tabIndex={0}
                    aria-expanded={isOpen}
                    aria-controls={`section-${nodeKey}`}
                    onClick={() => onToggle(nodeKey)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onToggle(nodeKey)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 2,
                        py: 1.25,
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: isOpen ? bgColors[level % bgColors.length] : 'transparent',
                        border: `1px solid ${isOpen ? alpha(palette.teal, 0.1) : 'transparent'}`,
                        '&:hover': {
                            bgcolor: alpha(palette.teal, 0.05),
                            borderColor: alpha(palette.teal, 0.2),
                        },
                        transition: 'all 0.2s ease',
                        position: 'relative',
                    }}
                >
                    <IconButton
                        size="small"
                        edge="start"
                        disableRipple
                        aria-label={isOpen ? 'Réduire' : 'Développer'}
                        sx={{
                            p: 0.5,
                            bgcolor: alpha(palette.teal, 0.08),
                            color: palette.teal,
                            '&:hover': {
                                bgcolor: alpha(palette.teal, 0.15),
                            },
                            borderRadius: 1.5,
                        }}
                    >
                        {isOpen ? (
                            <ExpandLess fontSize="small" />
                        ) : (
                            <ExpandMore fontSize="small" />
                        )}
                    </IconButton>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flex: 1 }}>
                        <Box
                            sx={{
                                p: 0.75,
                                borderRadius: 1.5,
                                bgcolor: alpha(palette.teal, 0.08),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${alpha(palette.teal, 0.15)}`,
                            }}
                        >
                            {icon}
                        </Box>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: isOpen ? 600 : 500,
                                color: isOpen ? palette.navy : palette.textPrimary,
                                fontSize: level === 0 ? '1rem' : '0.95rem',
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>

                    <Chip
                        label={count}
                        size="small"
                        sx={{
                            ml: 'auto',
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            height: 24,
                            minWidth: 32,
                            justifyContent: 'center',
                            bgcolor: alpha(palette.teal, 0.08),
                            color: palette.teal,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(palette.teal, 0.15)}`,
                        }}
                        aria-label={`${count} éléments`}
                    />

                    {!!right && (
                        <Box
                            onClick={(e) => e.stopPropagation()}
                            sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                            {right}
                        </Box>
                    )}
                </Paper>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box
                        id={`section-${nodeKey}`}
                        sx={{
                            ml: 2.5,
                            pl: 3,
                            mt: 1,
                            borderLeft: `1px dashed ${alpha(palette.teal, 0.15)}`,
                        }}
                    >
                        {children}
                    </Box>
                </Collapse>
            </Box>
        </Fade>
    );
};

const DocItem = ({ doc, onDownload, isDownloading, onPreview }) => {
    const palette = usePalette();
    const month = doc?.classification?.mois;
    const year = doc?.classification?.annee;
    const meta =
        month && year
            ? `${MONTHS_FR[+month] || `Mois ${month}`} ${year}`
            : year
            ? `${year}`
            : '';

    const tejState = String(doc?.tejState || 'ACTIVE').toUpperCase();
    const isCancelled = tejState === 'ANNULE';
    const isRectified = tejState === 'RECTIFIED';

    // Déterminer les couleurs et icônes en fonction de l'état
    let stateColor = palette.teal;
    let stateIcon = <InsertDriveFile />;
    let stateBgColor = alpha(palette.teal, 0.08);
    let stateBorderColor = alpha(palette.teal, 0.15);
    
    if (isCancelled) {
        stateColor = '#f44336';
        stateIcon = <Cancel />;
        stateBgColor = alpha('#f44336', 0.08);
        stateBorderColor = alpha('#f44336', 0.15);
    } else if (isRectified) {
        stateColor = palette.orange;
        stateIcon = <Edit />;
        stateBgColor = alpha(palette.orange, 0.08);
        stateBorderColor = alpha(palette.orange, 0.15);
    }

    return (
        <Zoom in style={{ transitionDelay: '30ms' }}>
            <Paper
                elevation={0}
                sx={{
                    mb: 0.75,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(palette.teal, 0.08)}`,
                    '&:hover': {
                        borderColor: stateColor,
                        boxShadow: `0 4px 12px ${alpha(stateColor, 0.08)}`,
                        backgroundColor: alpha(stateColor, 0.02),
                    },
                    transition: 'all 0.2s ease',
                    bgcolor: isCancelled || isRectified 
                        ? alpha(stateColor, 0.02) 
                        : 'transparent',
                }}
            >
                <ListItem
                    sx={{
                        px: 1.5,
                        py: 1,
                        '&:hover': {
                            bgcolor: isCancelled || isRectified
                                ? alpha(stateColor, 0.03) 
                                : alpha(palette.teal, 0.03),
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box
                            sx={{
                                p: 0.75,
                                borderRadius: 1.25,
                                bgcolor: stateBgColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `1px solid ${stateBorderColor}`,
                            }}
                        >
                            {React.cloneElement(stateIcon, {
                                fontSize: "small",
                                sx: { 
                                    color: stateColor,
                                    fontSize: '18px'
                                }
                            })}
                        </Box>
                    </ListItemIcon>

                    <ListItemText
                        primaryTypographyProps={{ 
                            sx: { 
                                fontSize: '0.925rem', 
                                fontWeight: 500,
                                color: (isCancelled || isRectified) ? palette.textSecondary : palette.textPrimary,
                                textDecoration: isCancelled ? 'line-through' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                                lineHeight: 1.4,
                            } 
                        }}
                        secondaryTypographyProps={{ 
                            sx: { 
                                fontSize: '0.825rem', 
                                color: palette.textSecondary,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.25,
                                lineHeight: 1.4,
                            } 
                        }}
                        primary={
                            <>
                                <Box component="span" sx={{ flex: 1, fontSize: '0.9rem' }}>
                                    {doc?.originalName || doc?.fileName || 'Document'}
                                </Box>
                                {isCancelled && (
                                    <Chip 
                                        size="small" 
                                        label="Annulé" 
                                        sx={{ 
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            height: 20,
                                            bgcolor: alpha('#f44336', 0.1),
                                            color: '#f44336',
                                            border: `1px solid ${alpha('#f44336', 0.2)}`,
                                            borderRadius: 1.5,
                                        }}
                                        icon={<Cancel fontSize="small" />}
                                    />
                                )}
                                {isRectified && (
                                    <Chip 
                                        size="small" 
                                        label="Rectifié" 
                                        sx={{ 
                                            fontWeight: 500,
                                            fontSize: '0.7rem',
                                            height: 20,
                                            bgcolor: alpha(palette.orange, 0.1),
                                            color: palette.orange,
                                            border: `1px solid ${alpha(palette.orange, 0.2)}`,
                                            borderRadius: 1.5,
                                        }}
                                        icon={<Edit fontSize="small" />}
                                    />
                                )}
                            </>
                        }
                        secondary={
                            <>
                                <Chip
                                    size="small"
                                    label={meta}
                                    sx={{ 
                                        height: 20, 
                                        fontSize: '0.75rem',
                                        bgcolor: alpha(palette.navy, 0.08),
                                        color: palette.navy,
                                        border: `1px solid ${alpha(palette.navy, 0.15)}`,
                                        borderRadius: 1.5,
                                    }}
                                    icon={<CalendarMonth fontSize="small" />}
                                />
                                {doc?.fileSize && (
                                    <Chip
                                        size="small"
                                        label={`${(doc.fileSize / 1024 / 1024).toFixed(2)} MB`}
                                        sx={{ 
                                            height: 20,
                                            fontSize: '0.75rem',
                                            bgcolor: alpha(palette.gold, 0.08),
                                            color: palette.gold,
                                            border: `1px solid ${alpha(palette.gold, 0.15)}`,
                                            borderRadius: 1.5,
                                        }}
                                    />
                                )}
                            </>
                        }
                    />

                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Aperçu rapide" arrow>
                            <IconButton
                                aria-label="Aperçu"
                                onClick={() => onPreview?.(doc)}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha(palette.navy, 0.08),
                                    color: palette.navy,
                                    '&:hover': { 
                                        bgcolor: alpha(palette.navy, 0.15),
                                    },
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s',
                                }}
                            >
                                <Visibility fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Télécharger le PDF" arrow>
                            <IconButton
                                aria-label="Télécharger"
                                onClick={() => onDownload(doc)}
                                disabled={isDownloading}
                                size="small"
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: alpha(palette.teal, 0.08),
                                    color: palette.teal,
                                    '&:hover': { 
                                        bgcolor: alpha(palette.teal, 0.15),
                                    },
                                    borderRadius: 1.5,
                                    transition: 'all 0.2s',
                                }}
                            >
                                {isDownloading ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <Download fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </ListItem>
            </Paper>
        </Zoom>
    );
};

// ========================= Main Component refondu ========================= //
const ClassementDocuments = () => {
    const palette = usePalette();
    const [searchParams] = useSearchParams();

    const focusClientId = searchParams.get('clientId');
    const focusType = searchParams.get('type');
    const focusYear = Number(searchParams.get('year'));
    const focusMonth = Number(searchParams.get('month'));

    const [data, setData] = useState([]);
    const [openState, setOpenState] = useState({});
    const [selectedClient, setSelectedClient] = useState(null);
    const [docStateFilter, setDocStateFilter] = useState('all');
    const [docTypeFilter, setDocTypeFilter] = useState('all');
    const [downloadingDocs, setDownloadingDocs] = useState(() => new Set());
    const [downloadingZipKeys, setDownloadingZipKeys] = useState(() => new Set());
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncLoading, setSyncLoading] = useState(false);
    
    // État pour le tri par code client
    const [sortConfig, setSortConfig] = useState({
        field: 'codeClient',
        direction: 'asc'
    });

    const { open, url, mime, name, error: previewError, openPreview, close } = usePreview();

    // Effet pour ouvrir automatiquement les sections selon les paramètres URL
    useEffect(() => {
        if (!focusClientId || !data.length) return;

        const entry = data.find(({ client }) => client?._id === focusClientId);
        if (!entry) return;

        const clientName = getClientDisplayName(entry.client);

        const nextOpen = {
            [`client-${clientName}`]: true,
        };

        if (focusType) nextOpen[`${clientName}-type-${focusType}`] = true;
        if (focusType && Number.isInteger(focusYear)) 
            nextOpen[`${clientName}-${focusType}-year-${focusYear}`] = true;

        if (focusType && Number.isInteger(focusYear) && Number.isInteger(focusMonth)) {
            nextOpen[`${clientName}-${focusType}-${focusYear}-month-${focusMonth}`] = true;
        }

        setOpenState(nextOpen);
    }, [focusClientId, focusType, focusYear, focusMonth, data]);

    // Chargement initial des données
    useEffect(() => {
        const ac = new AbortController();
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await fetch(API.LIST, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                    signal: ac.signal,
                });
                if (!res.ok) throw new Error('Erreur de chargement des documents');
                const json = await res.json();
                setData(json);
                setLoading(false);

            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError(err.message || 'Impossible de charger les documents');
                }
            } finally {
                console.log("finally")
            }
        })();
        return () => ac.abort();
    }, []);

    const handleRefresh = async () => {
        setSyncLoading(true);
        try {
            const res = await fetch(API.LIST, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });
            if (!res.ok) throw new Error('Erreur de rafraîchissement');
            const json = await res.json();
            setData(json);
            setSnackbar({
                open: true,
                severity: 'success',
                message: 'Documents rafraîchis avec succès'
            });
        } catch (err) {
            setSnackbar({
                open: true,
                severity: 'error',
                message: `Erreur: ${err.message}`
            });
        } finally {
            setSyncLoading(false);
        }
    };

    // Trier les clients par code client
    const sortedData = useMemo(() => {
        const sortableData = [...data];
        
        sortableData.sort((a, b) => {
            let aValue = a.client?.[sortConfig.field] || '';
            let bValue = b.client?.[sortConfig.field] || '';
            
            // Traitement spécial pour le code client (tri numérique)
            if (sortConfig.field === 'codeClient') {
                aValue = parseInt(aValue) || 0;
                bValue = parseInt(bValue) || 0;
            }
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
        
        return sortableData;
    }, [data, sortConfig]);

    const handleSort = (field) => {
        let direction = 'asc';
        
        if (sortConfig.field === field && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        
        setSortConfig({ field, direction });
    };

    // Préparer les options pour l'autocomplete (nom + code client pour la recherche)
    const clientOptions = useMemo(() => {
        const uniqueClients = new Map();
        
        sortedData.forEach(({ client }) => {
            if (!client) return;
            
            const displayName = getClientDisplayName(client);
            const nameOnly = getClientNameOnly(client);
            const code = getClientCode(client);
            
            if (!uniqueClients.has(displayName)) {
                uniqueClients.set(displayName, {
                    label: displayName,
                    name: nameOnly,
                    code: code,
                    clientId: client._id
                });
            }
        });
        
        return Array.from(uniqueClients.values());
    }, [sortedData]);

    const filteredData = useMemo(() => {
        if (!selectedClient) return sortedData;
        return sortedData.filter(({ client }) => {
            const displayName = getClientDisplayName(client);
            return displayName === selectedClient.label;
        });
    }, [sortedData, selectedClient]);

    const structured = useMemo(() => buildStructure(filteredData), [filteredData]);

    const filterDocumentsByState = useCallback((docs) => {
        if (docStateFilter === 'all') return docs;
        
        const wantActive = docStateFilter === 'active';
        const wantRectified = docStateFilter === 'rectified';
        const wantCancelled = docStateFilter === 'cancelled';
        
        return docs.filter((d) => {
            const st = String(d?.tejState || 'ACTIVE').toUpperCase();
            const isCancelled = st === 'ANNULE';
            const isRectified = st === 'RECTIFIED';
            const isActive = !isCancelled && !isRectified;
            
            if (wantActive) return isActive;
            if (wantRectified) return isRectified;
            if (wantCancelled) return isCancelled;
            return true;
        });
    }, [docStateFilter]);

    const totalDocs = useMemo(() => {
        return filteredData.reduce((sum, { documents = [] }) => {
            const matchingDocs = documents.filter((d) => {
                // Filtre par type
                if (docTypeFilter !== 'all') {
                    const docType = asType(d?.classification?.type);
                    if (docType !== docTypeFilter) return false;
                }
                // Filtre par état
                if (docStateFilter !== 'all') {
                    const st = String(d?.tejState || 'ACTIVE').toUpperCase();
                    const isCancelled = st === 'ANNULE';
                    const isRectified = st === 'RECTIFIED';
                    const isActive = !isCancelled && !isRectified;

                    if (docStateFilter === 'active' && !isActive) return false;
                    if (docStateFilter === 'rectified' && !isRectified) return false;
                    if (docStateFilter === 'cancelled' && !isCancelled) return false;
                }
                return true;
            });
            return sum + matchingDocs.length;
        }, 0);
    }, [filteredData, docStateFilter, docTypeFilter]);

    const totalClientsVisible = useMemo(() => {
        const visibleClients = new Set();
        
        Object.entries(structured).forEach(([client, types]) => {
            let hasVisibleDocs = false;

            Object.entries(types)
                .filter(([type]) => docTypeFilter === 'all' || type === docTypeFilter)
                .forEach(([, years]) => {
                Object.values(years).forEach((months) => {
                    Object.values(months).forEach((docs) => {
                        const visibleInMonth = filterDocumentsByState(docs);
                        if (visibleInMonth.length > 0) {
                            hasVisibleDocs = true;
                        }
                    });
                });
            });

            if (hasVisibleDocs) {
                visibleClients.add(client);
            }
        });

        return visibleClients.size;
    }, [structured, filterDocumentsByState, docTypeFilter]);

    const hasDataToShow = useMemo(() => {
        return Object.keys(structured).some(client => {
            return Object.entries(structured[client])
                .filter(([type]) => docTypeFilter === 'all' || type === docTypeFilter)
                .some(([, years]) => {
                return Object.values(years).some(months => {
                    return Object.values(months).some(docs => {
                        return filterDocumentsByState(docs).length > 0;
                    });
                });
            });
        });
    }, [structured, filterDocumentsByState, docTypeFilter]);

    const toggleOpen = useCallback((key) => {
        setOpenState((prev) => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const handleDownload = useCallback(
        async (doc) => {
            const id = doc?._id;
            if (!id || downloadingDocs.has(id)) return;

            setDownloadingDocs((prev) => new Set(prev).add(id));
            try {
                const res = await fetch(API.DOWNLOAD(id), {
                    method: 'GET',
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                if (!res.ok) {
                    const payload = await res.json().catch(() => ({}));
                    throw new Error(payload?.error || `Erreur HTTP ${res.status}`);
                }
                await downloadBlob(res, doc?.originalName || 'document');
            } catch (err) {
                setSnackbar({ 
                    open: true, 
                    message: `Téléchargement: ${err.message}`, 
                    severity: 'error' 
                });
            } finally {
                setDownloadingDocs((prev) => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            }
        },
        [downloadingDocs]
    );

    const navigate = useNavigate();

    const handleGoToIntegration = useCallback(
        ({ clientName, type, year, month }) => {
            // Extraire l'ID du client à partir du nom affiché
            const option = clientOptions.find(opt => opt.label === clientName);
            const clientId = option?.clientId;
            
            if (!clientId) {
                setSnackbar({ 
                    open: true, 
                    severity: 'error', 
                    message: 'Client introuvable pour la navigation.' 
                });
                return;
            }
            const t = asType(type);

            navigate(`/dashboard/integration?clientId=${clientId}&type=${t}&year=${year}&month=${month}`, {
                state: { from: 'classification', clientName, type: t, year, month },
            });
        },
        [clientOptions, navigate]
    );

    const handleDownloadZip = useCallback(
        async ({ clientName, type, year, month }) => {
            // Extraire l'ID du client à partir du nom affiché
            const option = clientOptions.find(opt => opt.label === clientName);
            const clientId = option?.clientId;
            
            if (!clientId) {
                setSnackbar({ 
                    open: true, 
                    severity: 'error', 
                    message: 'Client introuvable pour export ZIP.' 
                });
                return;
            }

            const bucketKey = [clientName, type || '_', year || '_', month || '_', docStateFilter].join('|');
            if (downloadingZipKeys.has(bucketKey)) return;
            setDownloadingZipKeys((prev) => new Set(prev).add(bucketKey));

            try {
                const t = type ? asType(type) : undefined;
                const res = await fetch(API.ZIP({ 
                    clientId, 
                    type: t, 
                    year, 
                    month, 
                    stateFilter: docStateFilter !== 'all' ? docStateFilter : undefined 
                }), {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                if (!res.ok) {
                    const payload = await res.json().catch(() => ({}));
                    throw new Error(payload?.error || `Erreur HTTP ${res.status}`);
                }
                await downloadAs(res, 'documents.zip');
                setSnackbar({ 
                    open: true, 
                    severity: 'success', 
                    message: 'Téléchargement ZIP terminé avec succès' 
                });
            } catch (err) {
                setSnackbar({ 
                    open: true, 
                    severity: 'error', 
                    message: `ZIP: ${err.message}` 
                });
            } finally {
                setDownloadingZipKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(bucketKey);
                    return next;
                });
            }
        },
        [clientOptions, downloadingZipKeys, docStateFilter]
    );

    const renderDocuments = useCallback(
        (tree) => (
            <List disablePadding>
                {Object.entries(tree).map(([client, types]) => {
                    const kClient = `client-${client}`;
                    
                    const clientDocCount = Object.entries(types)
                        .filter(([type]) => docTypeFilter === 'all' || type === docTypeFilter)
                        .reduce((total, [, years]) => {
                        return total + Object.values(years).reduce((yearTotal, months) => {
                            return yearTotal + Object.values(months).reduce((monthTotal, docs) => {
                                return monthTotal + filterDocumentsByState(docs).length;
                            }, 0);
                        }, 0);
                    }, 0);
                    
                    if (clientDocCount === 0) return null;
                    
                    return (
                        <SectionRow
                            key={kClient}
                            nodeKey={kClient}
                            icon={<Person sx={{ color: palette.teal, fontSize: '18px' }} />}
                            label={client}
                            count={clientDocCount}
                            isOpen={!!openState[kClient]}
                            onToggle={toggleOpen}
                            level={0}
                            right={
                                <Tooltip title="Télécharger tous les documents de ce client (.zip)" arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            aria-label="Télécharger ZIP - client"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDownloadZip({ clientName: client });
                                            }}
                                            disabled={
                                                clientDocCount === 0 || 
                                                downloadingZipKeys.has([client, '_', '_', '_', docStateFilter].join('|'))
                                            }
                                            sx={{
                                                width: 30,
                                                height: 30,
                                                bgcolor: alpha(palette.teal, 0.08),
                                                color: palette.teal,
                                                '&:hover': {
                                                    bgcolor: alpha(palette.teal, 0.15),
                                                },
                                                borderRadius: 1.5,
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            {downloadingZipKeys.has([client, '_', '_', '_', docStateFilter].join('|')) ? (
                                                <CircularProgress size={16} />
                                            ) : (
                                                <Download fontSize="small" />
                                            )}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            }
                        >
                            <List disablePadding sx={{ mt: 0.75 }}>
                                {Object.entries(types)
                                    .filter(([type]) => docTypeFilter === 'all' || type === docTypeFilter)
                                    .map(([type, years]) => {
                                    const kType = `${client}-type-${type}`;

                                    const typeDocCount = Object.values(years).reduce((yearTotal, months) => {
                                        return yearTotal + Object.values(months).reduce((monthTotal, docs) => {
                                            return monthTotal + filterDocumentsByState(docs).length;
                                        }, 0);
                                    }, 0);

                                    if (typeDocCount === 0) return null;
                                    
                                    return (
                                        <SectionRow
                                            key={kType}
                                            nodeKey={kType}
                                            icon={<Category sx={{ color: palette.green, fontSize: '18px' }} />}
                                            label={labelForType(type)}
                                            count={typeDocCount}
                                            isOpen={!!openState[kType]}
                                            onToggle={toggleOpen}
                                            level={1}
                                            right={
                                                <Tooltip title="Télécharger tous les documents de ce type (.zip)" arrow>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label="Télécharger ZIP - type"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDownloadZip({ clientName: client, type });
                                                            }}
                                                            disabled={
                                                                typeDocCount === 0 ||
                                                                downloadingZipKeys.has([client, type, '_', '_', docStateFilter].join('|'))
                                                            }
                                                            sx={{
                                                                width: 30,
                                                                height: 30,
                                                                bgcolor: alpha(palette.green, 0.08),
                                                                color: palette.green,
                                                                '&:hover': {
                                                                    bgcolor: alpha(palette.green, 0.15),
                                                                },
                                                                borderRadius: 1.5,
                                                                transition: 'all 0.2s',
                                                            }}
                                                        >
                                                            {downloadingZipKeys.has([client, type, '_', '_', docStateFilter].join('|')) ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <Download fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            }
                                        >
                                            <List disablePadding sx={{ mt: 0.75 }}>
                                                {Object.entries(years).map(([year, months]) => {
                                                    const kYear = `${client}-${type}-year-${year}`;
                                                    
                                                    const yearDocCount = Object.values(months).reduce((monthTotal, docs) => {
                                                        return monthTotal + filterDocumentsByState(docs).length;
                                                    }, 0);
                                                    
                                                    if (yearDocCount === 0) return null;
                                                    
                                                    return (
                                                        <SectionRow
                                                            key={kYear}
                                                            nodeKey={kYear}
                                                            icon={<CalendarMonth sx={{ color: palette.gold, fontSize: '18px' }} />}
                                                            label={year}
                                                            count={yearDocCount}
                                                            isOpen={!!openState[kYear]}
                                                            onToggle={toggleOpen}
                                                            level={2}
                                                            right={
                                                                <Tooltip title={`Télécharger tous les documents ${year} (.zip)`} arrow>
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            aria-label="Télécharger ZIP - année"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDownloadZip({ clientName: client, type, year });
                                                                            }}
                                                                            disabled={
                                                                                yearDocCount === 0 ||
                                                                                downloadingZipKeys.has([client, type, year, '_', docStateFilter].join('|'))
                                                                            }
                                                                            sx={{
                                                                                width: 30,
                                                                                height: 30,
                                                                                bgcolor: alpha(palette.gold, 0.08),
                                                                                color: palette.gold,
                                                                                '&:hover': {
                                                                                    bgcolor: alpha(palette.gold, 0.15),
                                                                                },
                                                                                borderRadius: 1.5,
                                                                                transition: 'all 0.2s',
                                                                            }}
                                                                        >
                                                                            {downloadingZipKeys.has([client, type, year, '_', docStateFilter].join('|')) ? (
                                                                                <CircularProgress size={16} />
                                                                            ) : (
                                                                                <Download fontSize="small" />
                                                                            )}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <List disablePadding sx={{ mt: 0.75 }}>
                                                                {Object.entries(months).map(([month, docs]) => {
                                                                    const kMonth = `${client}-${type}-${year}-month-${month}`;
                                                                    const filteredDocs = filterDocumentsByState(docs);
                                                                    const docsCount = filteredDocs.length;
                                                                    
                                                                    if (docsCount === 0) return null;
                                                                    
                                                                    return (
                                                                        <SectionRow
                                                                            key={kMonth}
                                                                            nodeKey={kMonth}
                                                                            icon={<Folder sx={{ color: palette.navy, fontSize: '18px' }} />}
                                                                            label={MONTHS_FR[+month] || `Mois ${month}`}
                                                                            count={docsCount}
                                                                            isOpen={!!openState[kMonth]}
                                                                            onToggle={toggleOpen}
                                                                            level={3}
                                                                            right={
                                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                                    <Tooltip title="Exporter ce mois vers la comptabilité" arrow>
                                                                                        <span>
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                aria-label="Exporter vers la comptabilité"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleGoToIntegration({ 
                                                                                                        clientName: client, 
                                                                                                        type, 
                                                                                                        year, 
                                                                                                        month 
                                                                                                    });
                                                                                                }}
                                                                                                disabled={docsCount === 0}
                                                                                                sx={{
                                                                                                    width: 30,
                                                                                                    height: 30,
                                                                                                    bgcolor: alpha(palette.navy, 0.08),
                                                                                                    color: palette.navy,
                                                                                                    '&:hover': {
                                                                                                        bgcolor: alpha(palette.navy, 0.15),
                                                                                                    },
                                                                                                    borderRadius: 1.5,
                                                                                                    transition: 'all 0.2s',
                                                                                                }}
                                                                                            >
                                                                                                <AccountBalance fontSize="small" />
                                                                                            </IconButton>
                                                                                        </span>
                                                                                    </Tooltip>

                                                                                    <Tooltip title="Télécharger les documents de ce mois (.zip)" arrow>
                                                                                        <span>
                                                                                            <IconButton
                                                                                                size="small"
                                                                                                aria-label="Télécharger ZIP - mois"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleDownloadZip({ 
                                                                                                        clientName: client, 
                                                                                                        type, 
                                                                                                        year, 
                                                                                                        month 
                                                                                                    });
                                                                                                }}
                                                                                                disabled={
                                                                                                    docsCount === 0 ||
                                                                                                    downloadingZipKeys.has([client, type, year, month, docStateFilter].join('|'))
                                                                                                }
                                                                                                sx={{
                                                                                                    width: 30,
                                                                                                    height: 30,
                                                                                                    bgcolor: alpha(palette.teal, 0.08),
                                                                                                    color: palette.teal,
                                                                                                    '&:hover': {
                                                                                                        bgcolor: alpha(palette.teal, 0.15),
                                                                                                    },
                                                                                                    borderRadius: 1.5,
                                                                                                    transition: 'all 0.2s',
                                                                                                }}
                                                                                            >
                                                                                                {downloadingZipKeys.has([client, type, year, month, docStateFilter].join('|')) ? (
                                                                                                    <CircularProgress size={16} />
                                                                                                ) : (
                                                                                                    <Download fontSize="small" />
                                                                                                )}
                                                                                            </IconButton>
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                </Box>
                                                                            }
                                                                        >
                                                                            <List disablePadding sx={{ mt: 0.75 }}>
                                                                                {filteredDocs.map((doc) => (
                                                                                    <DocItem
                                                                                        key={doc._id}
                                                                                        doc={doc}
                                                                                        onDownload={handleDownload}
                                                                                        onPreview={() => openPreview(doc)}
                                                                                        isDownloading={downloadingDocs.has(doc._id)}
                                                                                    />
                                                                                ))}
                                                                            </List>
                                                                        </SectionRow>
                                                                    );
                                                                })}
                                                            </List>
                                                        </SectionRow>
                                                    );
                                                })}
                                            </List>
                                        </SectionRow>
                                    );
                                })}
                            </List>
                        </SectionRow>
                    );
                })}
            </List>
        ),
        [
            downloadingDocs,
            openState,
            toggleOpen,
            handleDownloadZip,
            handleGoToIntegration,
            handleDownload,
            downloadingZipKeys,
            openPreview,
            filterDocumentsByState,
            palette,
            docStateFilter,
            docTypeFilter,
        ]
    );

    const hasAnyFilter = !!selectedClient || docStateFilter !== 'all' || docTypeFilter !== 'all';

    // Fonction pour filtrer les options dans l'autocomplete (par nom ET code)
    const filterOptions = (options, { inputValue }) => {
        const searchTerm = inputValue.toLowerCase();
        return options.filter(option => 
            option.label.toLowerCase().includes(searchTerm) ||
            option.code.toLowerCase().includes(searchTerm) ||
            option.name.toLowerCase().includes(searchTerm)
        );
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: palette.gradient,
                color: palette.textPrimary,
                py: 4,
                px: 2
            }}
        >
            <Container maxWidth="xl">
                <Card 
                    elevation={0}
                    sx={{ 
                        borderRadius: 4,
                        overflow: 'hidden',
                        bgcolor: 'white',
                        boxShadow: palette.shadow,
                        border: `1px solid ${alpha(palette.teal, 0.1)}`
                    }}
                >
                    {/* En-tête coloré avec dégradé */}
                    <Box
                        sx={{
                            py: 3,
                            px: 4,
                            background: `linear-gradient(135deg, ${palette.navy} 0%, rgba(20,45,80,0.85) 100%)`,
                            color: 'white',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                📁 Gestion des Documents
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                Parcourez vos certificats émis/reçus par client → type → année → mois
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Button 
                                variant="contained" 
                                startIcon={<Sync />} 
                                onClick={handleRefresh}
                                disabled={syncLoading || loading}
                                sx={{
                                    py: 1.2,
                                    px: 3,
                                    borderRadius: 2,
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    color: 'white',
                                    background: `linear-gradient(135deg, ${palette.teal} 0%, ${palette.teal2} 100%)`,
                                    textTransform: 'none',
                                    transition: 'all 0.3s',
                                    boxShadow: '0 4px 20px rgba(24, 182, 176, 0.3)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 30px rgba(24, 182, 176, 0.4)',
                                        background: `linear-gradient(135deg, ${palette.teal2} 0%, ${palette.green} 100%)`
                                    },
                                }}
                            >
                                {syncLoading ? 'Rafraîchissement...' : 'Rafraîchir'}
                            </Button>
                        </Box>
                    </Box>

                    <CardContent sx={{ p: 4 }}>
                        {loading && (
                            <>
                                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }}>
                                    <LinearProgress 
                                        sx={{ 
                                            height: 3,
                                            background: `linear-gradient(90deg, ${palette.teal} 0%, ${palette.green} 100%)`,
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 2,
                                            }
                                        }}
                                    />
                                </Box>
                                <Box 
                                    display="flex" 
                                    justifyContent="center" 
                                    alignItems="center" 
                                    height="300px"
                                    flexDirection="column"
                                    gap={2}
                                >
                                    <CircularProgress size={40} sx={{ color: palette.teal }} />
                                    <Typography variant="body1" sx={{ color: palette.textSecondary }}>
                                        Chargement des documents...
                                    </Typography>
                                </Box>
                            </>
                        )}

                        {!loading && (
                            <>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mb: 3 }}
                                >
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Filtres de recherche
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Affinez l'affichage des documents
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Chip 
                                            icon={<Person />}
                                            label={`${totalClientsVisible} client(s)`} 
                                            variant="outlined"
                                            sx={{ 
                                                fontWeight: 500,
                                                fontSize: '0.85rem',
                                                bgcolor: alpha(palette.teal, 0.08),
                                                color: palette.teal,
                                                border: `1px solid ${alpha(palette.teal, 0.2)}`,
                                                borderRadius: 1.5,
                                            }}
                                        />
                                        <Chip 
                                            icon={<InsertDriveFile />}
                                            label={`${totalDocs} document(s)`} 
                                            sx={{ 
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                bgcolor: alpha(palette.navy, 0.1),
                                                color: palette.navy,
                                                border: `1px solid ${alpha(palette.navy, 0.2)}`,
                                                borderRadius: 1.5,
                                            }}
                                        />
                                    </Stack>
                                </Stack>

                                <Divider 
                                    sx={{ 
                                        my: 3, 
                                        borderColor: alpha(palette.teal, 0.1),
                                    }} 
                                />

                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={2}
                                    alignItems={{ xs: 'stretch', md: 'center' }}
                                    justifyContent="space-between"
                                    sx={{ mb: 3 }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', md: 'row' }}
                                        spacing={2}
                                        sx={{ width: { xs: '100%', md: 'auto' } }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Autocomplete
                                                disablePortal
                                                options={clientOptions}
                                                getOptionLabel={(option) => option.label}
                                                filterOptions={filterOptions}
                                                sx={{ width: { xs: '100%', md: 400 } }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="🔍 Rechercher un client (nom ou code)..."
                                                        size="medium"
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            sx: { 
                                                                fontSize: '0.95rem', 
                                                                py: 0.5,
                                                                borderRadius: 2,
                                                                border: `1px solid ${alpha(palette.teal, 0.2)}`,
                                                                '&:hover': {
                                                                    borderColor: palette.teal,
                                                                }
                                                            },
                                                            startAdornment: (
                                                                <InputAdornment position="start">
                                                                    <Search fontSize="small" sx={{ color: palette.teal }} />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                        InputLabelProps={{ 
                                                            sx: { 
                                                                fontSize: '0.95rem',
                                                                color: palette.textPrimary,
                                                            } 
                                                        }}
                                                    />
                                                )}
                                                value={selectedClient}
                                                onChange={(_, v) => setSelectedClient(v)}
                                                clearOnEscape
                                                renderOption={(props, option) => (
                                                    <MenuItem {...props} sx={{ py: 1.25 }}>
                                                        <Person sx={{ mr: 1.5, color: palette.teal, fontSize: '20px' }} />
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {option.label}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: palette.textSecondary }}>
                                                                Code: {option.code || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </MenuItem>
                                                )}
                                            />
                                            
                                            <Tooltip title={`Trier par code client ${sortConfig.direction === 'asc' ? 'croissant' : 'décroissant'}`}>
                                                <IconButton
                                                    onClick={() => handleSort('codeClient')}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: alpha(palette.teal, 0.1),
                                                        color: palette.teal,
                                                        '&:hover': {
                                                            bgcolor: alpha(palette.teal, 0.2),
                                                        },
                                                        borderRadius: 2,
                                                    }}
                                                >
                                                    {sortConfig.direction === 'asc' ? 
                                                        <ArrowUpward fontSize="small" /> : 
                                                        <ArrowDownward fontSize="small" />
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        <TextField
                                            select
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.95rem' }}>
                                                    <Category fontSize="small" />
                                                    Type de document
                                                </Box>
                                            }
                                            value={docTypeFilter}
                                            onChange={(e) => setDocTypeFilter(e.target.value)}
                                            sx={{ width: { xs: '100%', md: 280 } }}
                                            SelectProps={{
                                                sx: { fontSize: '0.95rem' },
                                                renderValue: (value) => {
                                                    const option = DOC_TYPE_OPTIONS.find(opt => opt.value === value);
                                                    return (
                                                        <Typography sx={{ fontSize: '0.95rem' }}>{option?.label}</Typography>
                                                    );
                                                }
                                            }}
                                        >
                                            {DOC_TYPE_OPTIONS.map((opt) => (
                                                <MenuItem
                                                    key={opt.value}
                                                    value={opt.value}
                                                    sx={{ py: 1.25, fontSize: '0.95rem' }}
                                                >
                                                    <Typography sx={{ fontSize: '0.95rem' }}>{opt.label}</Typography>
                                                </MenuItem>
                                            ))}
                                        </TextField>

                                        <TextField
                                            select
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '0.95rem' }}>
                                                    <FilterList fontSize="small" />
                                                    État des documents
                                                </Box>
                                            }
                                            value={docStateFilter}
                                            onChange={(e) => setDocStateFilter(e.target.value)}
                                            sx={{ width: { xs: '100%', md: 280 } }}
                                            SelectProps={{
                                                sx: { fontSize: '0.95rem' },
                                                renderValue: (value) => {
                                                    const option = DOC_STATE_OPTIONS.find(opt => opt.value === value);
                                                    return (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            {option?.icon}
                                                            <Typography sx={{ fontSize: '0.95rem' }}>{option?.label}</Typography>
                                                        </Box>
                                                    );
                                                }
                                            }}
                                        >
                                            {DOC_STATE_OPTIONS.map((opt) => (
                                                <MenuItem
                                                    key={opt.value}
                                                    value={opt.value}
                                                    sx={{ py: 1.25, fontSize: '0.95rem' }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        {opt.icon}
                                                        <Typography sx={{ fontSize: '0.95rem' }}>{opt.label}</Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Stack>

                                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                                        <Button
                                            startIcon={<ClearAll />}
                                            variant="outlined"
                                            onClick={() => {
                                                setSelectedClient(null);
                                                setDocStateFilter('all');
                                                setDocTypeFilter('all');
                                            }}
                                            disabled={!hasAnyFilter}
                                            sx={{
                                                borderRadius: 2,
                                                fontWeight: 500,
                                                fontSize: '0.9rem',
                                                px: 2.5,
                                                py: 1,
                                                borderColor: alpha(palette.teal, 0.3),
                                                color: palette.teal,
                                                '&:hover': {
                                                    borderColor: palette.teal,
                                                    bgcolor: alpha(palette.teal, 0.08),
                                                }
                                            }}
                                        >
                                            Réinitialiser
                                        </Button>
                                    </Stack>
                                </Stack>

                                {error && (
                                    <Alert 
                                        severity="error" 
                                        sx={{ 
                                            mb: 3, 
                                            borderRadius: 2,
                                            border: `1px solid ${alpha('#f44336', 0.2)}`,
                                            backgroundColor: alpha('#f44336', 0.05),
                                            color: palette.textPrimary,
                                            boxShadow: palette.shadow,
                                            '& .MuiAlert-icon': {
                                                alignItems: 'center',
                                            }
                                        }}
                                        onClose={() => setError(null)}
                                    >
                                        <Typography variant="body1" fontWeight={500}>
                                            Erreur de chargement
                                        </Typography>
                                        <Typography variant="body2">
                                            {error}
                                        </Typography>
                                    </Alert>
                                )}

                                {!loading && !hasDataToShow && !error && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            textAlign: 'center',
                                            py: 6,
                                            px: 3,
                                            borderRadius: 2,
                                            bgcolor: alpha(palette.tealLight, 0.3),
                                            border: `1px dashed ${alpha(palette.teal, 0.3)}`,
                                        }}
                                    >
                                        <Box sx={{ fontSize: 48, mb: 1.5, opacity: 0.3, color: palette.teal }}>
                                            📭
                                        </Box>
                                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: palette.textPrimary, fontSize: '1.1rem' }}>
                                            Aucun document à afficher
                                        </Typography>
                                        <Typography variant="body2" sx={{ maxWidth: 400, mx: 'auto', color: palette.textSecondary, fontSize: '0.9rem' }}>
                                            {hasAnyFilter 
                                                ? 'Essayez de retirer les filtres pour voir tous les documents disponibles.'
                                                : 'Aucun document trouvé. Synchronisez vos clients pour importer des documents.'}
                                        </Typography>
                                    </Paper>
                                )}

                                {!loading && hasDataToShow && (
                                    <Fade in timeout={400}>
                                        <Box component="ul" sx={{ p: 0, m: 0 }}>
                                            {renderDocuments(structured)}
                                        </Box>
                                    </Fade>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </Container>

            <PreviewDialog open={open} onClose={close} url={url} mime={mime} name={name} error={previewError} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                TransitionComponent={Fade}
            >
                <Alert
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ 
                        width: '100%',
                        borderRadius: 2,
                        alignItems: 'center',
                        boxShadow: palette.shadow,
                        fontWeight: 500,
                        fontSize: '0.95rem',
                    }}
                    iconMapping={{
                        success: <CheckCircle fontSize="inherit" />,
                        error: <Cancel fontSize="inherit" />,
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ClassementDocuments;
